import React, { useState, useContext, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import shortenNumber from '@wix/da-shared-react/pkg/utils/shortenNumber';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import SearchIcon from '@wix/da-ds/pkg/Icons/24x24/Search';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import Flex from '@wix/da-ds/pkg/Flex';
import { MAX_SEARCH_QUERY_LENGTH } from '@wix/da-shared-react/pkg/utils/constants';
import { SEARCH_FIELD_ID } from '../../constants';
import SearchFilterTags from './SearchFilterTags';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

import s from './SearchBar.scss';

export interface Props {
  searchTerm: string;
  estTotalTotal?: number;
  filterTags?: any[];
  pageType?: string;
  selectedTags?: any[];
}

export type State = {
  search?: string;
};

const SearchBar: React.FC<Props> = ({
  searchTerm,
  filterTags,
  estTotalTotal,
  pageType,
  selectedTags,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const [inputValue, setInputValue] = useState(searchTerm ?? '');
  const [searchTermFromInput, setSearchTermFromInput] = useState<string>();
  const logBiEvent = useBiLogger();

  const handleChange = (value: string) => {
    setSearchTermFromInput(value.trim());
    setInputValue(value);
  };

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!searchTermFromInput) {
        return false;
      }
      logBiEvent({
        evid: BiEventType.SEARCH_CLICK,
        search_terms: searchTermFromInput,
        sectionname: 'top_bar',
      });
      const parsedQueryString = queryString.parse(window.location.search);
      const query = {
        ...parsedQueryString,
        q: searchTermFromInput,
        page: undefined,
      };
      window.location.search = queryString.stringify(query);
      return false;
    },
    [logBiEvent, searchTermFromInput]
  );

  const totalLabel = useMemo(() => {
    if (!estTotalTotal || estTotalTotal === 0) {
      return t('searchPage.noResults', { searchTerm });
    }

    if (isMobile) {
      return t('facetPage.resultsTotal', {
        count: estTotalTotal,
        formattedCount: shortenNumber(estTotalTotal),
        searchTerm,
      });
    }

    const formattedCount = shortenNumber(estTotalTotal);
    const count = estTotalTotal;
    if (pageType === 'artists') {
      return t('searchPage.searchTerm.artists', {
        count,
        formattedCount,
        searchTerm,
      });
    }

    return t('searchPage.searchTerm', {
      formattedCount,
      count,
    });
  }, [estTotalTotal, pageType, searchTerm, t, isMobile]);

  const filteredFilterTags = useMemo(() => {
    if (!filterTags) {
      return;
    }
    if (!selectedTags) {
      return filterTags;
    }
    const filterAndSelectedTags: any = [...selectedTags, ...filterTags];
    const tags: any = [];
    for (const filter of filterAndSelectedTags) {
      const selected = selectedTags.find(sel => sel.tag === filter.tag);
      tags.push({
        ...filter,
        selected: selected !== undefined,
      });
    }

    return tags.filter(
      (tag, idx, self) => idx === self.findIndex(item => item.tag === tag.tag)
    );
  }, [filterTags, selectedTags]);

  return (
    <div className={s['search-wrap']}>
      <div className={s['search-wrap-inner']}>
        {isMobile && (
          <form
            action="/search"
            onSubmit={handleSubmit}
            className={s['search-form']}
          >
            <TextInput
              id={SEARCH_FIELD_ID}
              label={t('browseHeader.search.input.label')}
              value={inputValue}
              maxLength={MAX_SEARCH_QUERY_LENGTH}
              onChange={e => handleChange(e.target.value)}
              postfix={
                <PlainButtonOrLink onClick={handleSubmit}>
                  <SearchIcon disableDefaultColors />
                </PlainButtonOrLink>
              }
              noBottomSpacing
              isLabelHidden
            />
          </form>
        )}

        <Flex alignItems="center" className={s['search-result']}>
          <span className={s['result-item']}>{totalLabel}</span>
          {filterTags && !isMobile && (
            <>
              <span className={s['separator']}>|</span>
              <span className={s['result-item']}>Tags:</span>
              <SearchFilterTags tags={filteredFilterTags} />
            </>
          )}
        </Flex>
      </div>
    </div>
  );
};
SearchBar.displayName = 'SearchBar';

export default SearchBar;
